import { useState, useCallback, useMemo } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import {
	type ConnectorState,
	useEnabledKnowledgeSources,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';

/**
 * Detects when the user types the name of an app that is not yet connected
 * and surfaces it so a connection tip can be shown.
 *
 * Returns:
 *  - `unconnectedSourceTip` – the matched unconnected connector, or null
 *  - `dismissTip` – clears the tip (call on successful OAuth connect)
 *  - `detectUnconnectedSource` – call from the editor onChange with the current text content
 */
export const useUnconnectedSourceTip = (): {
	unconnectedSourceTip: ConnectorState | null;
	dismissTip: () => void;
	detectUnconnectedSource: (textContent: string) => void;
} => {
	const [unconnectedSourceTip, setUnconnectedSourceTip] = useState<ConnectorState | null>(null);
	const [allKnowledgeSources] = useEnabledKnowledgeSources();

	// Pre-process once per store update — not on every keystroke:
	// filter to unconnected and lowercase the names.
	const unconnectedSources = useMemo(
		() =>
			allKnowledgeSources
				.filter((s) => !s.isUserOAuthed)
				.map((s) => ({ connector: s, lowerName: s.friendlyName.toLowerCase() })),
		[allKnowledgeSources],
	);

	const dismissTip = useCallback(() => {
		setUnconnectedSourceTip(null);
	}, []);

	const detectUnconnectedSource = useCallback(
		(textContent: string) => {
			if (unconnectedSources.length === 0) {
				setUnconnectedSourceTip(null);
				return;
			}

			const lowerText = textContent.toLowerCase();

			// Find the rightmost valid match — whichever app name appears latest
			// in the text is the one the user most recently typed.
			let bestConnector: ConnectorState | null = null;
			let bestIdx = -1;

			for (const { connector, lowerName } of unconnectedSources) {
				const idx = lowerText.lastIndexOf(lowerName);
				if (idx <= bestIdx) {
					continue;
				}
				// Ensure the match sits on word boundaries — the surrounding
				// character must be absent (start/end of string) or a non-word
				// character (whitespace, punctuation, brackets, etc.) so that
				// "slack" doesn't match inside "slackbot" or "reslack", but
				// "Google Drive," and "(Google Drive)" still match.
				const charBefore = lowerText[idx - 1];
				const charAfter = lowerText[idx + lowerName.length];
				const atStart = idx === 0 || /\W/.test(charBefore);
				const atEnd = charAfter === undefined || /\W/.test(charAfter);
				if (atStart && atEnd) {
					bestConnector = connector;
					bestIdx = idx;
				}
			}

			setUnconnectedSourceTip(bestConnector);
		},
		[unconnectedSources],
	);

	const [debouncedDetectUnconnectedSource] = useDebouncedCallback(detectUnconnectedSource, 200);

	return {
		unconnectedSourceTip,
		dismissTip,
		detectUnconnectedSource: debouncedDetectUnconnectedSource,
	};
};
